import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { NotebookPen } from "lucide-react";

/**
 * Weekly check-in nudge.
 *
 * Soft, non-blocking modal that appears at most once per session when:
 *   - User is logged in
 *   - User has finished onboarding
 *   - 7+ days have passed since either `last_checkin_at` or, if null,
 *     since signup (`created_at`)
 *
 * Suppressed on:
 *   - /onboarding (we just welcomed them)
 *   - /auth, /auth/callback (auth in-flight)
 *
 * Two CTAs:
 *   - Primary "פתח את המחברת" → calls /me/checkin (resets 7-day clock),
 *     navigates to /notebook.
 *   - Secondary "תזכיר לי בעוד כמה ימים" → /me/checkin/snooze (next nudge
 *     in ~3 days instead of 7).
 *   - Closing the dialog (X / overlay) ≡ snooze.
 *
 * Per-session guard via sessionStorage so dismissing once doesn't loop
 * if the backend write is delayed.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
const SUPPRESS_PATHS = ["/onboarding", "/auth", "/auth/callback"];
const SESSION_KEY = "torahya_checkin_shown_v1";

export default function WeeklyCheckin() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Compute whether the nudge is due. Returns the days-since-last-touch
  // number for display, plus a boolean.
  const due = useMemo(() => {
    if (!user || !user.onboarding_completed) return null;

    const now = Date.now();
    const lastTouchIso = user.last_checkin_at || user.created_at;
    if (!lastTouchIso) return null;

    const lastTouchMs = new Date(lastTouchIso).getTime();
    if (Number.isNaN(lastTouchMs)) return null;

    const daysSince = Math.floor((now - lastTouchMs) / DAY_MS);
    if (daysSince < 7) return null;
    return { daysSince };
  }, [user]);

  useEffect(() => {
    if (!due) return;
    // Suppress on auth/onboarding routes
    if (SUPPRESS_PATHS.some((p) => location.pathname.startsWith(p))) return;
    // Once per session is enough — even if the user just dismisses, we
    // don't pester them again until they reload.
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch {
      /* sessionStorage blocked — fall through and show once */
    }
    // Tiny delay so the nudge doesn't slam in on first paint.
    const t = setTimeout(() => {
      setOpen(true);
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
    }, 1800);
    return () => clearTimeout(t);
  }, [due, location.pathname]);

  const firstName = useMemo(() => {
    if (!user?.name) return "";
    return String(user.name).trim().split(/\s+/)[0];
  }, [user]);

  const handleCheckin = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.weeklyCheckin();
    } catch {
      /* non-fatal — still navigate */
    }
    setOpen(false);
    setBusy(false);
    navigate("/notebook");
  };

  const handleSnooze = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.snoozeWeeklyCheckin();
    } catch {
      /* non-fatal */
    }
    setBusy(false);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleSnooze();
        else setOpen(v);
      }}
    >
      <DialogContent
        dir={isEn ? "ltr" : "rtl"}
        data-testid="weekly-checkin-modal"
        className="max-w-md border-gold/40 bg-card/95 backdrop-blur-sm"
        onInteractOutside={(e) => {
          // Allow overlay click to snooze, but stop propagation to page
          e.preventDefault();
          handleSnooze();
        }}
      >
        <button
          type="button"
          onClick={handleSnooze}
          aria-label={t("weekly_checkin.close_aria")}
          data-testid="weekly-checkin-close"
          className="sr-only"
        >
          {t("weekly_checkin.close_aria")}
        </button>

        <div className="pt-4 text-center space-y-5">
          <div className="mx-auto h-14 w-14 rounded-full bg-gold/15 ring-1 ring-gold/40 flex items-center justify-center">
            <NotebookPen className="h-6 w-6 text-gold" strokeWidth={1.5} />
          </div>

          <div className="space-y-2">
            <h2
              className="font-heading text-2xl sm:text-3xl text-foreground leading-snug"
              data-testid="weekly-checkin-title"
            >
              {firstName
                ? t("weekly_checkin.title_with_name", { name: firstName })
                : t("weekly_checkin.title_default")}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("weekly_checkin.body")}
            </p>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={handleCheckin}
              disabled={busy}
              data-testid="weekly-checkin-open-notebook"
              className="sacred-btn-gold w-full py-5 min-h-[48px] text-base"
            >
              {t("weekly_checkin.cta_open")}
            </Button>
            <button
              type="button"
              onClick={handleSnooze}
              disabled={busy}
              data-testid="weekly-checkin-snooze"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2 disabled:opacity-50"
            >
              {t("weekly_checkin.cta_snooze")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
